import { useEffect, useRef, useState } from "react";
import { apiFetch, uploadVideo } from "../lib/auth";
import { inputClass, labelClass } from "../admin-ui";

type UploadStatus = "none" | "waiting" | "preparing" | "ready" | "errored";

const statusLabels: Record<UploadStatus, string> = {
  none: "",
  waiting: "Subiendo a Mux…",
  preparing: "Procesando el video…",
  ready: "Listo para reproducir.",
  errored: "La codificación falló.",
};

/**
 * The video provider seam.
 *
 * Everything above this component knows only `{ url, onChange }`. Mux is used
 * when the API reports it configured; otherwise the field falls back to the
 * local upload route so a machine without Mux credentials is still usable.
 */
export function VideoField({
  lessonId,
  url,
  onChange,
}: {
  lessonId: string;
  url: string;
  onChange: (url: string) => void;
}) {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<UploadStatus>("none");
  const [error, setError] = useState("");
  const pollRef = useRef<number | null>(null);

  // Poll only while an upload is in flight. The webhook is the primary signal;
  // this endpoint reconciles against Mux when one never arrives.
  useEffect(() => {
    if (status !== "waiting" && status !== "preparing") return;
    const tick = async () => {
      try {
        const result = await apiFetch<{
          status: UploadStatus;
          playbackId?: string;
        }>(`/api/admin/videos/${lessonId}/status`);
        setStatus(result.status);
        if (result.status === "ready" && result.playbackId)
          onChange(result.playbackId);
      } catch {
        // A failed poll is not fatal; the next tick retries.
      }
    };
    pollRef.current = window.setInterval(tick, 4000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [status, lessonId, onChange]);

  /** Sends the file straight to Mux, so it never passes through our API. */
  const uploadToMux = async (file: File) => {
    const { uploadUrl } = await apiFetch<{ uploadUrl: string }>(
      "/api/admin/videos/mux-upload",
      { method: "POST", body: JSON.stringify({ lessonId }) },
    );
    await new Promise<void>((resolve, reject) => {
      const request = new XMLHttpRequest();
      request.open("PUT", uploadUrl);
      request.upload.onprogress = (event) => {
        if (event.lengthComputable)
          setProgress(Math.round((event.loaded / event.total) * 100));
      };
      request.onload = () =>
        request.status < 400
          ? resolve()
          : reject(new Error(`upload failed: ${request.status}`));
      request.onerror = () => reject(new Error("upload failed"));
      request.send(file);
    });
    setStatus("waiting");
  };

  const handleFile = async (file: File) => {
    setUploading(true);
    setError("");
    setProgress(0);
    try {
      await uploadToMux(file);
    } catch (muxError) {
      // Mux not configured (503) or unreachable — fall back to the local
      // upload route so the editor keeps working on a bare environment.
      console.warn("[video] falling back to local upload", muxError);
      try {
        onChange(await uploadVideo(file, lessonId));
        setStatus("ready");
      } catch {
        setError("No pudimos subir el video. Intenta de nuevo.");
      }
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <label className={`${labelClass} sm:col-span-2`}>
        Video (id de Mux, Vimeo o enlace directo)
        <input
          name="videoUrl"
          value={url}
          onChange={(event) => onChange(event.target.value)}
          placeholder="Se completa solo al subir un archivo"
          className={inputClass}
        />
      </label>
      <label className={`${labelClass} sm:col-span-2`}>
        Subir archivo de video
        <input
          type="file"
          accept="video/*"
          disabled={uploading}
          className={inputClass}
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) handleFile(file);
          }}
        />
        {uploading && (
          <span className="mt-1 block normal-case">Subiendo… {progress}%</span>
        )}
        {!uploading && status !== "none" && (
          <span className="mt-1 block normal-case">{statusLabels[status]}</span>
        )}
        {error && (
          <span className="bg-bubble border-ink border-chip mt-1 block p-2 normal-case">
            {error}
          </span>
        )}
      </label>
    </>
  );
}
